import { useState, useCallback } from 'react';
import { ChakraProvider } from '@chakra-ui/react';
import { useToastContext } from '@/contexts/toastContext';
import useApiFetch from '@/hooks/useApiFetch';
import usersApi from '@/services/user';
import { chakraExtendedTheme } from '@/styles/theme';
import { PaginationProps } from '@/trio-ui/Pagination/Pagination.component';
import { FetchApiIds } from '@/types';
import { ResponseApiAllUsers, User } from '@/types/api';
import UsersManagementComponent from './UsersManagement.component';

const UsersManagement = (): JSX.Element => {
  const { showSuccessFeedback, showErrorFeedback } = useToastContext();
  const [pagination, setPagination] = useState<
    Pick<PaginationProps, 'currentPage' | 'itemsPerPage'>
  >({
    currentPage: 1,
    itemsPerPage: 10,
  });
  const [search, setSearch] = useState<string>('');
  const [isNewUserFormModalOpen, setIsNewUserFormModal] = useState<boolean>();
  const [editingUser, setEditingUser] = useState<User>();

  const handleChangePageHandler = (page: number): void => {
    setPagination((prev) => ({
      ...prev,
      currentPage: page,
    }));
  };

  const handleChageRecordsPerPage = (value: number): void => {
    setPagination({
      currentPage: 1,
      itemsPerPage: value,
    });
  };

  const handleChangeSearch = (value: string): void => {
    setSearch(value);
    setPagination((prev) => ({
      currentPage: 1,
      itemsPerPage: prev.itemsPerPage,
    }));
  };

  const allUsersResponse = useApiFetch<ResponseApiAllUsers>(
    [FetchApiIds.GET_ALL_UERS, search, pagination.currentPage, pagination.itemsPerPage],
    async () =>
      usersApi.getAllUsers({
        term: search,
        page: pagination.currentPage,
        take: pagination.itemsPerPage,
      })
  );

  const handleAddUserClick = (): void => {
    setEditingUser(undefined);
    setIsNewUserFormModal(true);
  };

  const handleCloseForm = (): void => {
    setIsNewUserFormModal(false);
  };

  const handleEditUserClick = (user: User): void => {
    setEditingUser(user);
    setIsNewUserFormModal(true);
  };

  const handleResendInvite = useCallback(
    async (user: User): Promise<void> => {
      try {
        const result = await usersApi.resendInvite(user.email, user.name);
        if (result.tempPassword) {
          showSuccessFeedback(
            `Temporary password for ${user.email}: ${result.tempPassword} — User will be asked to reset on first login.`
          );
        } else {
          showSuccessFeedback(`Invite resent to ${user.email}`);
        }
      } catch {
        showErrorFeedback('Failed to resend invite');
      }
    },
    [showSuccessFeedback, showErrorFeedback]
  );

  return (
    <ChakraProvider theme={chakraExtendedTheme}>
      <UsersManagementComponent
        onChangePageHandler={handleChangePageHandler}
        onChangeRecordsPerPage={handleChageRecordsPerPage}
        count={allUsersResponse.data?.users.count || 0}
        lastPage={allUsersResponse.data?.users.lastPage || 1}
        isLoading={allUsersResponse.isLoading}
        search={search}
        onSearchChange={handleChangeSearch}
        users={allUsersResponse.data?.users.data}
        isNewUserFormModalOpen={isNewUserFormModalOpen}
        refreshUsersList={allUsersResponse.refetch}
        onAddUserClick={handleAddUserClick}
        onCloseForm={handleCloseForm}
        onEditUserClick={handleEditUserClick}
        onResendInvite={handleResendInvite}
        editingUser={editingUser}
        {...pagination}
      />
    </ChakraProvider>
  );
};

export default UsersManagement;
